import React, { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { SearchableSelectType } from 'components/inputs/searchableSelect';
import AampeCreateSelectDropdown from 'components/shared/AampeCreateSelectDropdown/AampeCreateSelectDropdown';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import routes from '../../constants/routes';

import { RewardFunction } from 'models/formula.model';

interface RewardFunctionSelectProps {
    name: string;
    rewardFunctions: RewardFunction[];
    handleChange: (value: string) => void;
    initialValue?: string | null;
}

const RewardFunctionSelect: FC<
    React.PropsWithChildren<RewardFunctionSelectProps>
> = ({ name, rewardFunctions, handleChange, initialValue }) => {
    const [selected, setSelected] = useState<string>(initialValue ?? '');
    const options = useMemo<SearchableSelectType[]>(
        () =>
            rewardFunctions.map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [rewardFunctions],
    );

    const selectionOption: SearchableSelectType | undefined = options.find(
        (item) => item.value === selected,
    );

    return (
        <StyledDiv>
            <AampeCreateSelectDropdown
                infoDescription="What events should our agents prioritize during learning."
                label="Reward function"
                name={name}
                options={options}
                value={selectionOption}
                onChange={(field, value) => {
                    const v = (value as SearchableSelectType).value as string;
                    setSelected(v);
                    handleChange(v);
                }}
                isClearable={false}
                creatable={false}
            />
            {rewardFunctions.length === 0 && (
                <EmptyStateText>
                    No reward functions available.{' '}
                    <EmptyStateLink to={routes.rewardFunctionsMain}>
                        Create one in System Setup.
                    </EmptyStateLink>
                </EmptyStateText>
            )}
        </StyledDiv>
    );
};

const StyledDiv = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(3),
    height: '100%',
    width: '100%',
});

const EmptyStateText = styled.p({
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.textAccessibleWG01,
});

const EmptyStateLink = styled(Link)({
    color: aampeTheme.colors.solidA01,
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline',
    },
});

export default RewardFunctionSelect;
